import { useCallback, useEffect, useRef, useState } from "react";
import { api, authHeaders } from "services/api.service";

export type WarehouseLabelPayload = Record<string, unknown>;

// Released after a minute so the download can start before the blob URL is gone.
const OBJECT_URL_LIFETIME_MS = 60_000;
const PREVIEW_DELAY_MS = 300;

// Sent without the axios client: its interceptors would camel-case the image away, and the payload is already
// in the snake_case the platform reads.
async function postLabel(path: "preview" | "pdf", payload: WarehouseLabelPayload): Promise<Blob> {
  const response = await fetch(`${api.defaults.baseURL}/warehouse_labels/${path}`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.error?.message ?? `Could not make the label (status ${response.status})`);
  }

  return response.blob();
}

export const fetchWarehouseLabelPreview = (payload: WarehouseLabelPayload, page: number) =>
  postLabel("preview", { ...payload, page });

export const fetchWarehouseLabelPdf = (payload: WarehouseLabelPayload) => postLabel("pdf", payload);

// The PNG of one label in the batch, refetched shortly after the form stops changing.
export function useWarehouseLabelPreview(payload: WarehouseLabelPayload | null, page: number) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const key = payload ? JSON.stringify(payload) : null;

  useEffect(() => {
    if (!key) {
      setImageUrl(null);
      setError(null);
      setIsLoading(false);
      return undefined;
    }

    // Drop the previous preview immediately. Its blob URL is revoked by the cleanup below, and a
    // failed preview must not stay on screen while the next one is still waiting to be fetched.
    setImageUrl(null);
    setError(null);
    setIsLoading(true);

    let cancelled = false;
    let url: string | null = null;
    const timer = setTimeout(async () => {
      setIsLoading(true);
      try {
        const blob = await fetchWarehouseLabelPreview(JSON.parse(key), page);
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setImageUrl(url);
        setError(null);
      } catch (caught) {
        if (cancelled) return;
        setImageUrl(null);
        setError(caught instanceof Error ? caught.message : "Could not make the label");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }, PREVIEW_DELAY_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (url) URL.revokeObjectURL(url);
    };
  }, [key, page]);

  return { imageUrl, error, isLoading };
}

function pdfFilename(payload: WarehouseLabelPayload): string {
  const code = Array.isArray(payload.codes) ? payload.codes[0] : null;
  const safe = typeof code === "string" ? code.replace(/[^\w.-]+/g, "-") : "location";
  return `${safe}-label.pdf`;
}

function savePdf(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), OBJECT_URL_LIFETIME_MS);
}

// Saves the print-ready PDF. A download does not depend on a pop-up window.
export function useWarehouseLabelPdf() {
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  const downloadPdf = async (payload: WarehouseLabelPayload) => {
    setIsLoadingPdf(true);
    setPdfError(null);
    try {
      savePdf(await fetchWarehouseLabelPdf(payload), pdfFilename(payload));
    } catch (caught) {
      setPdfError(caught instanceof Error ? caught.message : "Could not make the PDF");
    } finally {
      setIsLoadingPdf(false);
    }
  };

  return { downloadPdf, isLoadingPdf, pdfError };
}

export interface WarehouseLabelPrinter {
  id: string;
  name: string;
  kind: string;
  label_kind: string;
  local?: boolean;
}

export interface WarehouseLabelPrintJob {
  id: string;
  state: "queued" | "waiting" | "printing" | "done" | "error";
  printer: string;
  done: number;
  total: number;
  detail: string;
  current: string | null;
  error: string | null;
}

const POLL_MS = 1000;

async function requestJson<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${api.defaults.baseURL}/warehouse_labels/${path}`, {
    ...init,
    headers: { ...authHeaders(), "Content-Type": "application/json" },
  });
  const body = await response.json().catch(() => null);
  if (!response.ok)
    throw new Error(body?.error?.message ?? `Could not reach the label service (status ${response.status})`);

  return body as T;
}

export function useWarehouseLabelPrinters(enabled: boolean) {
  const [printers, setPrinters] = useState<WarehouseLabelPrinter[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    requestJson<{ printers: WarehouseLabelPrinter[] }>("printers")
      .then((body) => !cancelled && setPrinters(body.printers))
      .catch(
        (caught) => !cancelled && setError(caught instanceof Error ? caught.message : "Could not list the printers"),
      );
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { printers, error };
}

// Starts a print job on the platform, which drives the printer in the background, and follows it until it ends.
export function useWarehouseLabelPrint() {
  const [job, setJob] = useState<WarehouseLabelPrintJob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const alive = useRef(true);
  // The print currently being followed. A poll for any other id is dropped, including one already
  // in flight when a newer print starts. clearTimeout cannot cancel that request.
  const followedId = useRef<string | null>(null);
  const printGeneration = useRef(0);

  useEffect(
    () => () => {
      alive.current = false;
      clearTimeout(timer.current);
    },
    [],
  );

  const follow = useCallback((id: string) => {
    timer.current = setTimeout(async () => {
      if (followedId.current !== id) return;
      try {
        const { job: next } = await requestJson<{ job: WarehouseLabelPrintJob }>(`print_jobs/${id}`);
        if (!alive.current || followedId.current !== id) return;
        setJob((current) => (current?.id === id ? next : current));
        if (next.state === "done" || next.state === "error") {
          followedId.current = null;
          return;
        }
      } catch (caught) {
        if (!alive.current || followedId.current !== id) return;
        const message = caught instanceof Error ? caught.message : "Lost track of the print job";
        setError(message);
        setJob((current) => (current?.id === id ? { ...current, state: "error", error: message } : current));
        followedId.current = null;
        return;
      }
      if (followedId.current === id) follow(id);
    }, POLL_MS);
  }, []);

  const print = async (payload: WarehouseLabelPayload, printerId: string, cutMode: string) => {
    const generation = ++printGeneration.current;
    clearTimeout(timer.current);
    followedId.current = null;
    setIsStarting(true);
    setError(null);
    setJob(null);
    try {
      const { job: started } = await requestJson<{ job: WarehouseLabelPrintJob }>("print", {
        method: "POST",
        body: JSON.stringify({ ...payload, printer_id: printerId, ...(cutMode ? { cut_mode: cutMode } : {}) }),
      });
      if (!alive.current || generation !== printGeneration.current) return;
      setJob(started);
      // The server can finish the job before this response. Polling it again lets a later failure
      // replace a finished job, and a late success can overwrite a newer print.
      if (started.state === "done" || started.state === "error") return;
      followedId.current = started.id;
      follow(started.id);
    } catch (caught) {
      if (!alive.current || generation !== printGeneration.current) return;
      setError(caught instanceof Error ? caught.message : "Could not start printing");
    } finally {
      if (generation === printGeneration.current) setIsStarting(false);
    }
  };

  const busy = isStarting || (job !== null && job.state !== "done" && job.state !== "error");
  return { print, job, error, busy };
}
